import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { T } from "../../theme";

/* ─── animations ─── */
const popIn = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.15); }
  80%  { transform: scale(0.95); }
  100% { transform: scale(1); opacity: 1; }
`;

const ringPulse = keyframes`
  0%   { transform: scale(1);    opacity: .5; }
  100% { transform: scale(1.55); opacity: 0;  }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ════════ LAYOUT ════════ */
const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${T.bg};
`;

const Center = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: clamp(60px, 9vw, 108px) clamp(20px, 5vw, 64px) 80px;
`;

/* ── check icon ── */
const CheckOuter = styled.div`
  position: relative;
  width: 88px;
  height: 88px;
  margin-bottom: 32px;

  &::before, &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(74,222,128,.45);
    animation: ${ringPulse} 2s ease-out infinite;
  }
  &::after { animation-delay: 1s; }
`;

const CheckInner = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: rgba(74,222,128,.12);
  border: 2px solid rgba(74,222,128,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #4ade80;
  animation: ${popIn} 0.6s cubic-bezier(.34,1.56,.64,1) both;
  position: relative;
  z-index: 1;
`;

/* ── heading ── */
const Title = styled.h1`
  font-size: clamp(1.9rem, 4.5vw, 2.8rem);
  font-weight: 900;
  letter-spacing: -0.035em;
  line-height: 1.1;
  margin-bottom: 14px;
  animation: ${fadeUp} 0.4s 0.1s ease both;

  background: linear-gradient(135deg, ${T.text} 30%, rgba(74,222,128,.85) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Sub = styled.p`
  font-size: clamp(0.92rem, 2vw, 1.05rem);
  color: ${T.muted};
  max-width: 440px;
  line-height: 1.7;
  margin-bottom: 44px;
  animation: ${fadeUp} 0.4s 0.15s ease both;

  strong { color: #4ade80; font-weight: 700; }
`;

/* ── steps card ── */
const StepsCard = styled.div`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 20px;
  padding: 28px 28px 24px;
  max-width: 440px;
  width: 100%;
  text-align: left;
  margin-bottom: 36px;
  animation: ${fadeUp} 0.4s 0.2s ease both;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #22c55e, #4ade80, #86efac);
  }
`;

const StepsEyebrow = styled.p`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #4ade80;
  margin-bottom: 18px;
`;

const StepList = styled.ol`
  list-style: none;
  counter-reset: step;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StepItem = styled.li`
  counter-increment: step;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  font-size: 0.88rem;
  color: ${T.muted};
  line-height: 1.55;

  &::before {
    content: counter(step);
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: rgba(74,222,128,.1);
    border: 1px solid rgba(74,222,128,.25);
    color: #4ade80;
    font-size: 0.7rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0;
  }
`;

/* ── buttons ── */
const Btns = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeUp} 0.4s 0.25s ease both;
`;

const BtnPrimary = styled.button`
  background: var(--purple);
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 13px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 4px 16px rgba(155,81,255,.28);
  transition: filter .14s, transform .12s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
`;

const BtnGhost = styled.button`
  background: transparent;
  border: 1.5px solid ${T.border};
  color: ${T.muted};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 13px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color .14s, color .14s;
  &:hover { border-color: var(--purple); color: ${T.text}; }
`;

/* ══════════════════════════════════
   COMPONENT
══════════════════════════════════ */
export default function Obrigado() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Candidatura enviada! · Guebly";
    return () => { document.title = "Guebly Carreiras — Vagas abertas"; };
  }, []);

  return (
    <Page>
      <Header />
      <Center id="main-content">
        <CheckOuter aria-hidden="true">
          <CheckInner>✓</CheckInner>
        </CheckOuter>

        <Title>Candidatura enviada!</Title>
        <Sub>
          Recebemos tudo certinho. Nossa equipe vai analisar seu perfil e
          te responder em até <strong>5 dias úteis</strong> — em todos os casos, sem exceção.
        </Sub>

        <StepsCard>
          <StepsEyebrow>O que acontece agora</StepsEyebrow>
          <StepList>
            <StepItem>Você receberá um e-mail de confirmação em instantes</StepItem>
            <StepItem>Nossa equipe analisa seu perfil e currículo</StepItem>
            <StepItem>Se avançar, você receberá convite para entrevista com o RH</StepItem>
            <StepItem>Acompanhe o status da sua candidatura a qualquer momento</StepItem>
          </StepList>
        </StepsCard>

        <Btns>
          <BtnPrimary onClick={() => navigate("/acompanhar")}>
            Acompanhar candidatura →
          </BtnPrimary>
          <BtnGhost onClick={() => navigate("/")}>
            Ver outras vagas
          </BtnGhost>
        </Btns>
      </Center>
      <Footer />
    </Page>
  );
}
